import { useState } from "react";
import { Button } from "@/components/ax/Button";
import { EPISTEMIC, EpistemicRow } from "@/components/ax/EpistemicRow";
import { Field } from "@/components/ax/Field";
import { RadioChips } from "@/components/ax/RadioChips";
import { useI18n } from "@/i18n";

export const FactsSection = ({ w, items }) => {
  const { t } = useI18n();
  const epistemicOptions = EPISTEMIC.map((v) => ({ value: v, label: t(`epistemic.${v}`), mono: true }));
  const [draft, setDraft] = useState({ text: "", epistemic: "" });
  const [error, setError] = useState();
  const busy = w.busy === "item";

  const add = async () => {
    if (draft.text.trim().length < 3) return setError(t("err.item"));
    if (await w.addItem(draft.text, draft.epistemic)) setDraft({ text: "", epistemic: "" });
  };

  return (
    <section className="mt-12" aria-labelledby="ws-facts" data-testid="workspace-facts">
      <h2 id="ws-facts" className="caption m-0 mb-3 text-ax-muted">{t("ws.facts")}</h2>
      <div className="grid gap-6 md:grid-cols-3">
        {EPISTEMIC.map((ep) => (
          <ul key={ep} data-testid={`facts-group-${ep.toLowerCase()}`} className="m-0 flex list-none flex-col gap-3 p-0 empty:hidden">
            {items
              .filter((i) => i.epistemic === ep)
              .map((i) => (
                <EpistemicRow key={i.id} item={i} onChange={w.changeStatus} busy={busy} />
              ))}
          </ul>
        ))}
      </div>
      <div className="mt-6 flex flex-col gap-4">
        <Field
          multiline
          label={t("items.addLabel")}
          maxLength={280}
          value={draft.text}
          onChange={(e) => {
            setDraft((d) => ({ ...d, text: e.target.value }));
            setError();
          }}
          error={error}
          testId="ws-item-text"
        />
        <RadioChips
          legend={t("items.status")}
          name="ws-add-status"
          testId="ws-item-status"
          value={draft.epistemic}
          onChange={(v) => setDraft((d) => ({ ...d, epistemic: v }))}
          options={epistemicOptions}
        />
        <Button
          disabled={!draft.text || !draft.epistemic || busy}
          busy={busy}
          onClick={add}
          data-testid="ws-item-add-button"
          className="self-start"
        >
          {t("items.add")}
        </Button>
        {w.errors.item && (
          <p role="alert" data-testid="ws-item-error" className="m-0 text-sm leading-5 text-ax-error">
            {w.errors.item}
          </p>
        )}
      </div>
    </section>
  );
};

export const OptionsSection = ({ w }) => {
  const { t } = useI18n();
  const d = w.decision;
  return (
    <section className="mt-12" aria-labelledby="ws-options" data-testid="workspace-options">
      <h2 id="ws-options" className="caption m-0 mb-3 text-ax-muted">{t("opt.list")}</h2>
      <ul className="m-0 grid list-none gap-3 p-0 md:grid-cols-3">
        {d.options.map((o) => (
          <li key={o.id} data-testid={`ws-option-${o.id}`} className="rounded-[12px] border border-ax-border bg-ax-surface p-4 shadow">
            <p className="m-0 text-base font-medium leading-6 text-ax-text [overflow-wrap:anywhere]">{o.name}</p>
            {o.description && <p className="m-0 mt-1 text-sm leading-5 text-ax-text-2 [overflow-wrap:anywhere]">{o.description}</p>}
            <p className="m-0 mt-2 font-mono text-[13px] leading-[18px] text-ax-muted">{t(`rev.${o.reversibility}`)}</p>
            {!w.committed && (
              <Button
                variant="ghost"
                disabled={w.busy === "option"}
                onClick={() => w.deleteOption(o.id)}
                data-testid={`ws-option-delete-${o.id}`}
                className="mt-1 min-h-[44px] px-0"
              >
                {t("ws.delete")}
              </Button>
            )}
          </li>
        ))}
      </ul>
      {d.options.length < 2 && (
        <p data-testid="ws-options-hint" className="m-0 mt-3 text-sm leading-5 text-ax-muted">{t("opt.min")}</p>
      )}
      {w.errors.option && <p role="alert" className="m-0 mt-3 text-sm leading-5 text-ax-error">{w.errors.option}</p>}
    </section>
  );
};
